import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import {
    useControllerInitialization,
    organizationAndProjectSelection,
    buildGenericMenu,
    GlobalPageLayout,
    buildNavProps,
    generateNavigation,
    getDownloadableQueries,
    headerTitle,
    buildHeaderComponents,
} from "../utils/utils";
import { applyThemeWithPriority, buildStaticLinks, hasModule } from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import useUpdateThemes from "../../hooks/useUpdateThemes";
import proxy from "../../routes/proxy";
import { matchRoutes } from "react-router-config";
import { asideRepositoryNavigationByModule } from "../Repository/navigation";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    toggleMenu,
    setLocale,
    removeModal,
    getCustomCsvTablesAvailable,
    getAvailableConfigSetsAction,
    getAvailableEntitiesAction,
    getAvailableCdmFieldsAction,
    createImportProfileMappingAction,
    getImportProfileMappingByProfileAction,
    updateImportProfileMappingAction,
    runCustomTemplateMappingAction,
} from "../../store/actions";
import ColumnMappingCanvas from "../../store/views/MappingFlow/ColumnMappingCanvas";
import { filteredQueriesByModules } from "../../routes/manageRouting";
import Header from "../custom_csv_raw_data/CustomCsvRawDataHeader";
import Connect from "colbi_web_ui/lib/components/Connect";
import { mapProperties } from "colbi_web_ui/lib/utils";

const ColumnMappingFlow = (props) => {
    const dispatch = useDispatch();
    const dispatchGetCustomCsvTablesAvailable = useCallback(
        (...args) => dispatch(getCustomCsvTablesAvailable(...args)),
        [dispatch],
    );
    const dispatchGetAvailableConfigSets = useCallback(
        (projectModules) => dispatch(getAvailableConfigSetsAction(projectModules)),
        [dispatch],
    );
    const dispatchGetAvailableEntities = useCallback(
        (configSet) => dispatch(getAvailableEntitiesAction(configSet)),
        [dispatch],
    );
    const dispatchGetAvailableCdmFields = useCallback(
        (configSet, entityName) => dispatch(getAvailableCdmFieldsAction(configSet, entityName)),
        [dispatch],
    );
    const dispatchCreateImportProfileMapping = useCallback(
        (payload) => dispatch(createImportProfileMappingAction(payload)),
        [dispatch],
    );
    const dispatchGetImportProfileMappingByProfile = useCallback(
        (profileId) => dispatch(getImportProfileMappingByProfileAction(profileId)),
        [dispatch],
    );
    const dispatchUpdateImportProfileMapping = useCallback(
        (mappingId, data) => dispatch(updateImportProfileMappingAction(mappingId, data)),
        [dispatch],
    );
    const dispatchRunCustomTemplateMapping = useCallback(
        (payload) => dispatch(runCustomTemplateMappingAction(payload)),
        [dispatch],
    );

    const {
        modules,
        route,
        location,
        matchParams,
        projectId,
        locale,
        fetchResults,
        fetchStatus,
        i18n,
        userInformation,
        history,
        pushModal,
        reload,
    } = useControllerInitialization(props);

    const [availableConfigSets, setAvailableConfigSets] = useState(null);

    useEffect(() => {
        if (!modules?.length) return;
        const moduleNames = modules
            .map((m) => (typeof m === "string" ? m : m?.name))
            .filter(Boolean);
        if (!moduleNames.length) return;
        dispatchGetAvailableConfigSets(moduleNames).then((result) => {
            setAvailableConfigSets(result || []);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modules]);

    let { fiscalYear, importId, entityRawData } = matchParams;
    const profileId = parseInt(entityRawData, 10) || undefined;
    const hasNewNavigation = hasModule(modules, "new_navigation");
    const {
        menuOrganizationProject,
        organizationProjects,
        project,
        fiscalYear: finalFiscalYear,
        importId: finalImportId,
        queryStatus,
    } = organizationAndProjectSelection({
        fetchResults,
        fetchStatus,
        component: ColumnMappingFlow,
        matchParams,
        props,
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId,
    });

    const { loading = true, success = false } = queryStatus || {};
    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);

    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes, "ColumnMappingFlow");
    }, [organizationProjects, projectId, updateThemes]);

    fiscalYear = finalFiscalYear ?? fiscalYear;
    importId = finalImportId ?? importId;

    const { navData, params, routeQueries } = buildGenericMenu(
        modules,
        fetchResults,
        locale,
        fiscalYear,
        projectId,
        importId,
        project,
        location,
        matchParams,
        route,
    );

    const { items, headerItems, metadata, filters, rawDataEdit } = mapProperties(
        {
            items: "query(0).items",
            headerItems: "query(0).headerItems",
            metadata: "query(0).metadata",
            filters: "query(0).filters",
            rawDataEdit: "query(0).rawDataEdit",
        },
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const title = headerTitle(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        fiscalYear,
        importId,
    );

    const headerComponents = buildHeaderComponents(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const downloadableQueries = getDownloadableQueries(matchRoutes, route, location, params);

    const { navigation } = generateNavigation({
        modules,
        asideRepositoryNavigationByModule,
        route,
        fetchResults,
        fetchStatus,
        component: ColumnMappingFlow,
        params,
        fiscalYear,
        importId,
        props,
        navData,
        i18n,
        locale,
    });

    const { navProps, staticLinks } = buildNavProps(
        buildStaticLinks,
        navigation,
        history,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
        modules,
        undefined,
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: { ...params, fiscalYear, importId },
        staticLinks,
        repositoryStatus: { status: 1 },
    };

    if (loading) {
        return LoadingComponent;
    }

    return (
        <GlobalPageLayout
            globalLayoutProps={globalLayoutProps}
            modules={modules}
            headerProps={{
                fiscalYears: undefined,
                importIds: undefined,
                downloadableQueries,
                title: title ? i18n`${title}` : i18n`Column Mapping`,
                headerComponents,
                titleBarActions: headerComponents?.length ? headerComponents.map((hc, idx) => (
                    <hc.Component
                        key={`titlebar-action-${idx}`}
                        pushModal={pushModal}
                        i18n={i18n}
                        reload={reload}
                        projectId={projectId}
                        user={userInformation}
                        {...hc.props}
                    />
                )) : undefined,
            }}
            headerComponentsMapper={() => (headerComponentProperties) => (
                <headerComponentProperties.Component
                    key="column-mapping"
                    pushModal={pushModal}
                    i18n={i18n}
                    reload={reload}
                    projectId={projectId}
                    user={userInformation}
                    {...headerComponentProperties.props}
                />
            )}
            fetch={props.fetch}
            route={route}
            matchParams={matchParams}
            menu={props.menu}
            isLoadingContent={() => false}
        >
            <Connect to={["search", "downloading"]}>
                {({ search, downloading }) => (
                    <>
                        <Header
                            getCustomCsvTablesAvailable={dispatchGetCustomCsvTablesAvailable}
                            fiscalYears={null}
                            importIds={null}
                            downloadableQueries={downloadableQueries}
                            disableEditModeButton={true}
                            hasNewNavigation={hasNewNavigation}
                        />
                    </>
                )}
            </Connect>
            <ColumnMappingCanvas
                items={items}
                headerItems={headerItems}
                metadata={metadata}
                filters={filters}
                rawDataEdit={rawDataEdit}
                profileId={profileId}
                projectId={projectId}
                fiscalYear={fiscalYear}
                importId={parseInt(importId, 10) || undefined}
                projectModules={project?.modules?.length ? project.modules : modules || []}
                availableConfigSets={availableConfigSets}
                onFetchConfigSets={dispatchGetAvailableConfigSets}
                onFetchEntities={dispatchGetAvailableEntities}
                onFetchCdmFields={dispatchGetAvailableCdmFields}
                onFetchExistingMapping={dispatchGetImportProfileMappingByProfile}
                onCreateMapping={dispatchCreateImportProfileMapping}
                onUpdateMapping={dispatchUpdateImportProfileMapping}
                onRunMapping={dispatchRunCustomTemplateMapping}
            />
        </GlobalPageLayout>
    );
};

ColumnMappingFlow.queries = (args, userModulesByProject) => {
    const queries = filteredQueriesByModules(userModulesByProject);
    const deepCopyQueries = JSON.parse(JSON.stringify(queries));
    if (deepCopyQueries) {
        //needed to change arguments to the respective value
        deepCopyQueries.forEach((query) =>
            Object.keys(query.args).forEach((k) =>
                query.args[k] ? query.args[k] : (query.args[k] = args[k] || query.args[k]),
            ),
        );
    }

    const allQueries = [
        {
            resource: "menu_organizations_projects",
            body: "id,name,typeEntity,nestedAccounts{id,name,typeEntity}",
            forceRefresh: true,
        },
        {
            resource: "organization_projects_by_projectId",
            args: {
                projectId: args.projectId,
            },
            body: "id,name,maxFiscalYear,fiscalYears,calendarYears,mosaicThemeConfig,projects{id,name,fiscalYears,status,taxRegistrationNumber,active,projectExpired,metadata,mosaicThemeConfig,modules{navigation_by_import_id,name,default_route,default_module}},organizationExpired, organizationActive, maxFiscalYear",
            forceRefresh: true,
        },
        {
            resource: "custom_csv_raw_data",
            args: {
                entity: args.entityRawData || "",
                projectId: args.projectId,
                fiscalYear: args.fiscalYear,
                importId: args.importId,
            },
            body: "items,headerItems,metadata{total},rawDataEdit",
            isDatagridReq: true,
        },
    ];
    return [...allQueries, ...deepCopyQueries];
};

export default connect(
    ({ user, fetch, menu }) => ({ user, fetch, menu }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
    },
)(withRouter(ColumnMappingFlow));
